import * as React from "react";
import { cloneDeep, isEqual } from "lodash";
import {
    BeforeAiSessionNoteReview,
    useAiSessionNoteReview,
} from "../../../context/AiSessionNoteReviewContext";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../hooks/permissions";
import { UserPermisions, UserRole } from "../../../utils/types/user";
import {
    AiReviewFormField,
    getAiReviewFieldFeedbackId,
    getAiReviewFieldsNeedingAttention,
    isAiSessionNotesEnabled,
} from "../helpers/aiSessionNotes";

type SubmitOverrideReason = "attention" | "unavailable";

interface SubmitOverride<T> {
    reason: SubmitOverrideReason;
    reviewedValues: T;
    reviewRequestKey: number;
}

interface FocusRequest {
    field: AiReviewFormField;
}

interface UseAiSessionNotePreSubmitReviewOptions<T> {
    beforeReview: BeforeAiSessionNoteReview;
    getValues(): T;
    onSubmit(values: T): Promise<unknown> | unknown;
    reviewedValuesRef: React.MutableRefObject<T | null>;
}

export function useAiSessionNotePreSubmitReview<T>({
    beforeReview,
    getValues,
    onSubmit,
    reviewedValuesRef,
}: UseAiSessionNotePreSubmitReviewOptions<T>) {
    const {
        errorMessage,
        focusRequestKey,
        isPending,
        isStale,
        review,
        runReview,
    } = useAiSessionNoteReview();
    const canUseAi = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_NOTES_AI_REVIEW,
    ]);
    const isClinician = useDoesUserHaveRole([
        UserRole.PROVIDER,
        UserRole.SUPER_ADMIN,
    ]);
    const [submitOverride, setSubmitOverride] =
        React.useState<SubmitOverride<T> | null>(null);
    const [focusRequest, setFocusRequest] = React.useState<FocusRequest | null>(
        null
    );
    const shouldReviewBeforeSubmit =
        isAiSessionNotesEnabled() && canUseAi && isClinician;
    const hasMatchingReviewResult =
        submitOverride?.reason === "attention"
            ? review !== null
            : errorMessage !== null;
    const activeSubmitOverride =
        shouldReviewBeforeSubmit &&
        submitOverride &&
        submitOverride.reviewRequestKey === focusRequestKey &&
        hasMatchingReviewResult &&
        isEqual(submitOverride.reviewedValues, getValues())
            ? submitOverride.reason
            : null;

    React.useEffect(() => {
        if (!focusRequest) return;
        const feedback = document.getElementById(
            getAiReviewFieldFeedbackId(focusRequest.field)
        );
        feedback?.scrollIntoView({ block: "center" });
        feedback?.focus({ preventScroll: true });
    }, [focusRequest]);

    const handleSubmit = async (values: T) => {
        if (isPending) return;

        if (!shouldReviewBeforeSubmit) {
            await onSubmit(values);
            return;
        }

        if (activeSubmitOverride) {
            setSubmitOverride(null);
            await onSubmit(values);
            return;
        }

        const reviewedValues = reviewedValuesRef.current;
        const hasFreshReview =
            review !== null &&
            !isStale &&
            reviewedValues !== null &&
            isEqual(reviewedValues, getValues());
        let currentReview = hasFreshReview ? review : null;
        let currentReviewRequestKey = focusRequestKey;

        if (!currentReview) {
            let savedForReview = false;
            let accessDenied = false;
            setSubmitOverride(null);
            currentReviewRequestKey = focusRequestKey + 1;
            currentReview = await runReview(
                async () => {
                    savedForReview = await beforeReview();
                    return savedForReview;
                },
                () => {
                    accessDenied = true;
                }
            );

            if (!currentReview) {
                if (accessDenied) {
                    if (
                        savedForReview &&
                        reviewedValuesRef.current !== null &&
                        isEqual(reviewedValuesRef.current, getValues())
                    ) {
                        await onSubmit(getValues());
                    }
                    return;
                }
                if (savedForReview && reviewedValuesRef.current) {
                    setSubmitOverride({
                        reason: "unavailable",
                        reviewedValues: cloneDeep(reviewedValuesRef.current),
                        reviewRequestKey: currentReviewRequestKey,
                    });
                }
                return;
            }
        }

        const currentValues = getValues();
        const currentReviewedValues = reviewedValuesRef.current;
        if (
            currentReviewedValues === null ||
            !isEqual(currentReviewedValues, currentValues)
        ) {
            return;
        }

        const attentionFields =
            getAiReviewFieldsNeedingAttention(currentReview);
        if (attentionFields.length > 0) {
            setSubmitOverride({
                reason: "attention",
                reviewedValues: cloneDeep(currentReviewedValues),
                reviewRequestKey: currentReviewRequestKey,
            });
            setFocusRequest({
                field: attentionFields[0],
            });
            return;
        }

        setSubmitOverride(null);
        await onSubmit(currentValues);
    };

    let submitButtonLabel = "Submit for review";
    if (isPending) submitButtonLabel = "Checking note…";
    if (!isPending && activeSubmitOverride === "attention") {
        submitButtonLabel = "Submit anyway";
    }
    if (!isPending && activeSubmitOverride === "unavailable") {
        submitButtonLabel = "Submit without AI review";
    }

    return {
        handleSubmit,
        isSubmitDisabled: isPending,
        submitButtonLabel,
    };
}
